import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { API_BASE_URL } from '../../../config';

interface ZoneEntry {
  zone: string;
  seconds: number;
  minutes: number;
  pct: number;
}

interface AggregateZonesData {
  has_data: boolean;
  activity_count: number;
  range: string;
  power_zones?: ZoneEntry[];
  hr_zones?: ZoneEntry[];
  power_activity_count?: number;
  hr_activity_count?: number;
}

interface ActivityZonesChartProps {
  targetUserId?: number | null;
}

type ChartRange = '30d' | '90d' | '6mo' | '1yr' | 'all';

const rangeOptions: { value: ChartRange; label: string }[] = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '6mo', label: '6mo' },
  { value: '1yr', label: '1yr' },
  { value: 'all', label: 'All' },
];

const POWER_COLORS = ['#66bb6a', '#42a5f5', '#ffa726', '#ef5350', '#ab47bc', '#ec407a', '#ff1744'];
const HR_COLORS = ['#66bb6a', '#42a5f5', '#ffa726', '#ef5350', '#ab47bc'];

const formatMinutes = (mins: number): string => {
  if (mins >= 60) {
    const h = Math.floor(mins / 60);
    const m = Math.round(mins % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  return `${Math.round(mins)}m`;
};

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 4, color: '#333' }}>{d.zone}</div>
      <div style={{ color: '#555' }}>Time: {formatMinutes(d.minutes)}</div>
      <div style={{ color: '#555' }}>{d.pct}% of total</div>
    </div>
  );
};

const ActivityZonesChart: React.FC<ActivityZonesChartProps> = ({ targetUserId }) => {
  const [data, setData] = useState<AggregateZonesData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [range, setRange] = useState<ChartRange>('90d');

  const fetchZones = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (targetUserId) params.set('target_user_id', String(targetUserId));
      const url = `${API_BASE_URL}/api/analytics/aggregate-zones?${params.toString()}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) {
        setFailed(true);
        setData(null);
        return;
      }
      const json: AggregateZonesData = await res.json();
      setData(json);
      setFailed(false);
    } catch {
      setFailed(true);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, targetUserId]);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  if (failed) return null;

  if (loading) {
    return (
      <div className="analytics-card" style={{ marginTop: 20 }}>
        <h3>Zone Distribution</h3>
        <div className="analytics-skeleton analytics-skeleton-chart" style={{ marginBottom: 0 }} />
      </div>
    );
  }

  if (!data || !data.has_data) return null;

  const hasPower = data.power_zones && data.power_zones.length > 0;
  const hasHR = data.hr_zones && data.hr_zones.length > 0;
  if (!hasPower && !hasHR) return null;

  // Shorter zone labels for chart (e.g. "Z1 Recovery" → "Z1 Recovery")
  const shortLabel = (zone: string) => zone;

  return (
    <div className="analytics-card" style={{ marginTop: 20 }}>
      <h3>Zone Distribution</h3>
      <div className="analytics-range-buttons">
        {rangeOptions.map((opt) => (
          <button
            key={opt.value}
            className={`analytics-range-btn ${range === opt.value ? 'active' : ''}`}
            onClick={() => setRange(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
        Across {data.activity_count} activities
      </div>
      <div className="zones-charts-row">
        {hasPower && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>
              Power Zones
              {data.power_activity_count && <span style={{ fontWeight: 400, color: '#999' }}> ({data.power_activity_count} rides)</span>}
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.power_zones} layout="vertical" margin={{ left: 10, right: 50, top: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={(v) => formatMinutes(v)} />
                <YAxis
                  type="category"
                  dataKey="zone"
                  tick={{ fontSize: 11, fill: '#666' }}
                  width={120}
                  tickFormatter={shortLabel}
                />
                <Tooltip content={<CustomTooltip />} isAnimationActive={false} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="minutes" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 10, fill: '#888', formatter: ((v: any) => `${Number(v).toFixed(0)}m`) as any }}>
                  {data.power_zones!.map((_, i) => (
                    <Cell key={i} fill={POWER_COLORS[i % POWER_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {hasHR && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>
              HR Zones
              {data.hr_activity_count && <span style={{ fontWeight: 400, color: '#999' }}> ({data.hr_activity_count} rides)</span>}
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data.hr_zones} layout="vertical" margin={{ left: 10, right: 50, top: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#999' }} tickFormatter={(v) => formatMinutes(v)} />
                <YAxis
                  type="category"
                  dataKey="zone"
                  tick={{ fontSize: 11, fill: '#666' }}
                  width={120}
                  tickFormatter={shortLabel}
                />
                <Tooltip content={<CustomTooltip />} isAnimationActive={false} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
                <Bar dataKey="minutes" radius={[0, 4, 4, 0]} label={{ position: 'right', fontSize: 10, fill: '#888', formatter: ((v: any) => `${Number(v).toFixed(0)}m`) as any }}>
                  {data.hr_zones!.map((_, i) => (
                    <Cell key={i} fill={HR_COLORS[i % HR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityZonesChart;
